import React from "react";
import { observer } from "mobx-react-lite";
import { renderStore } from "../../store/renderStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import WallOutlineSegment from "./WallOutlineSegment";
import { LineMaterial } from "three-stdlib";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";

// Material cache to avoid creating duplicate materials
// Key format: "color|linewidth|dashed|dashSize|gapSize|resolution.x|resolution.y"
const materialCache = new Map<string, LineMaterial>();

/**
 * Get or create a cached LineMaterial with the given properties.
 * This reduces memory usage and improves performance when many segments share the same appearance.
 * Resolution dimensions are included in the cache key to prevent stale resolution issues.
 */
export function getCachedMaterial(
  color: number,
  linewidth: number,
  dashed: boolean,
  resolution: THREE.Vector2
): LineMaterial {
  const dashSize = 0.3;
  const gapSize = 0.15;
  // Include resolution dimensions in cache key to prevent stale resolution
  const key = `${color}|${linewidth}|${dashed}|${dashSize}|${gapSize}|${resolution.x}|${resolution.y}`;

  let material = materialCache.get(key);
  if (!material) {
    material = new LineMaterial({
      color,
      linewidth,
      resolution: resolution.clone(), // Clone to avoid shared reference issues
    });

    if (dashed) {
      material.dashed = true;
      material.dashSize = dashSize;
      material.gapSize = gapSize;
    }

    material.needsUpdate = true;
    materialCache.set(key, material);
  } else {
    // Update existing material's resolution if it changed (shouldn't happen with new key, but safety check)
    if (material.resolution.x !== resolution.x || material.resolution.y !== resolution.y) {
      material.resolution.set(resolution.x, resolution.y);
      material.needsUpdate = true;
    }
  }

  return material;
}

/**
 * Clear the material cache and dispose all cached materials.
 * Should be called when the feature flag is toggled or on scene reset.
 */
export function clearMaterialCache() {
  materialCache.forEach(material => material.dispose());
  materialCache.clear();
}

/**
 * Container component that renders all outline segments for all walls.
 *
 * This component follows the same pattern as JointPolygonStructure, rendering
 * outline segments with gaps at connection points. The segments are pre-calculated
 * and stored in renderStore.outlineSegmentsMap.
 */
const WallOutlineStructure: React.FC = observer(() => {
  const canvasWidth = useThree((state) => state.size.width);
  const canvasHeight = useThree((state) => state.size.height);

  // Clear material cache on unmount
  React.useEffect(() => {
    return () => {
      clearMaterialCache();
    };
  }, []);

  // Subscribe to size changes and update cached materials' resolution
  React.useEffect(() => {
    // Update all cached materials' resolution on canvas resize
    materialCache.forEach(material => {
      material.resolution.set(canvasWidth, canvasHeight);
      material.needsUpdate = true;
    });
  }, [canvasWidth, canvasHeight]);

  // Always render outline segments (edge-based mode is always active)

  // Get all walls
  const walls = Array.from(floorplannerStore.wallsMap.values());

  return (
    <group>
      {walls.map((wall) => {
        // Get outline segments for this wall
        const outlineSegments = renderStore.getOutlineSegments(wall.id);

        if (!outlineSegments) {
          // No segments found for this wall
          return null;
        }

        //console.log(`  WallOutlineStructure: Wall ${wall.id} - segments: left=${outlineSegments.leftSegments.length}, right=${outlineSegments.rightSegments.length}, endcaps=${outlineSegments.endCapSegments.length}, geometryVersion=${outlineSegments.geometryVersion}`);

        // Calculate translate offset for optimized group drag
        const translateOffset = renderStore.isWallTranslating(wall.id)
          ? renderStore.groupTranslateOffset
          : { x: 0, y: 0 };

        // Calculate rotation offset for optimized group rotation
        const isRotating = renderStore.isWallRotating(wall.id);
        const rotationData = isRotating
          ? { angle: renderStore.groupRotationAngle, center: renderStore.groupRotationCenter }
          : null;

        // Calculate final position with both translate and rotation offsets
        let finalPosition = [translateOffset.x, translateOffset.y, 0] as [number, number, number];
        let groupRotation = [0, 0, 0] as [number, number, number];
        if (rotationData) {
          const { angle, center } = rotationData;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const rotationCompX = center.x * (1 - cos) + center.y * sin;
          const rotationCompY = center.y * (1 - cos) - center.x * sin;
          finalPosition = [translateOffset.x + rotationCompX, translateOffset.y + rotationCompY, 0];
          groupRotation = [0, 0, angle];
        }

        return (
          <group key={`wall-${wall.id}-outlines`} position={finalPosition} rotation={groupRotation}>
            {/* Render left outline segments */}
            {outlineSegments.leftSegments.map((segment, index) => (
              <WallOutlineSegment
                key={`${segment.id}-left-${index}`}
                segment={segment}
                getCachedMaterial={getCachedMaterial}
              />
            ))}

            {/* Render right outline segments */}
            {outlineSegments.rightSegments.map((segment, index) => (
              <WallOutlineSegment
                key={`${segment.id}-right-${index}`}
                segment={segment}
                getCachedMaterial={getCachedMaterial}
              />
            ))}

            {/* Render endcap segments */}
            {outlineSegments.endCapSegments.map((segment, index) => (
              <WallOutlineSegment
                key={`${segment.id}-endcap-${index}`}
                segment={segment}
                getCachedMaterial={getCachedMaterial}
              />
            ))}
          </group>
        );
      })}
    </group>
  );
});

WallOutlineStructure.displayName = "WallOutlineStructure";

export default WallOutlineStructure;
